import {useState} from 'react'
import {Link, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {KairosMark} from '@/components/kairos-mark'
import {MicroLabel} from '@/components/period-selector'
import {ListEmptyState} from '@/components/section-card'
import {PorQue} from '@/components/ui/por-que'
import {sgsstPageGuard} from '@/components/no-access-state'
import {useBusiness, useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {translateApiError} from '@/lib/errors'
import {
  downloadPerfilExcel,
  usePerfilAggregates,
  type PerfilDistribution,
} from '@/hooks/salud/perfil_riesgo/use-perfil-agregados'

const ENUM_VAR_TO_NS: Record<string, string> = {
  modalidad: 'modalidad',
  tipo_contrato: 'tipoContrato',
  transporte_principal: 'transporte',
  estado_civil: 'estadoCivil',
  tipo_vivienda: 'tipoVivienda',
}

// Reusa las etiquetas del formulario de la ficha para que agregado y formulario no diverjan.
const FICHA_VAR_TO_NS: Record<string, string> = {
  genero: 'generoOptions',
  nivel_educativo: 'nivelEducativoOptions',
}

function tierLabelKey(tier: string): string {
  if (tier === 'laboral') return 'perfilAgregados.tierLaboral'
  if (tier === 'personal') return 'perfilAgregados.tierPersonal'
  return 'perfilAgregados.tierSalud'
}

export function PerfilAgregadosPage() {
  const {t} = useTranslation()
  const {businessId} = useParams()
  const {isLoading: businessLoading} = useBusiness(businessId)
  const canOperate = useCapabilities(businessId).sgsst_operar
  const {data: aggregates, isLoading} = usePerfilAggregates(canOperate ? businessId : undefined)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  const guard = sgsstPageGuard(businessLoading || isLoading, canOperate, aggregates)
  if (guard) return guard
  if (!aggregates) return null

  function valueLabel(variable: string, key: string): string {
    const ns = ENUM_VAR_TO_NS[variable]
    if (ns) return t(`perfilSociodemografico.${ns}.${key}`)
    const fichaNs = FICHA_VAR_TO_NS[variable]
    if (fichaNs) return t(`ficha.${fichaNs}.${key}`)
    const banded = t(`perfilAgregados.value.${variable}.${key}`, {defaultValue: ''})
    if (banded) return banded
    return t(`perfilAgregados.value.${key}`, {defaultValue: key})
  }

  async function handleDownload() {
    setDownloadError(null)
    try {
      await downloadPerfilExcel(businessId!)
    } catch (err) {
      setDownloadError(translateApiError(err))
    }
  }

  const tiers: string[] = ['laboral', 'personal', 'salud']
  const empty = aggregates.sealed_count === 0
  // Nivel B (spec §2.2): the ficha section has its OWN source and denominator — it can
  // carry data even with zero sealed perfiles, so it renders independently of `empty`.
  const fichaDists = aggregates.distributions.filter((d) => d.tier === 'ficha')
  const fichaHasData = fichaDists.some((d) => d.n > 0)

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-foreground">
            <KairosMark />
            <h1 className="font-heading text-xl">{t('perfilAgregados.pageTitle')}</h1>
          </div>
          <p className="mt-1 font-mono text-xs text-muted-foreground">
            {t('perfilAgregados.pageSubtitle', {year: aggregates.year})}
          </p>
          <p className="mt-1 font-mono text-xs text-muted-foreground" data-testid="perfil-agregados-coverage">
            {t('perfilAgregados.coverageLine', {
              sealed: aggregates.sealed_count,
              active: aggregates.active_count,
            })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" data-testid="perfil-agregados-export" onClick={handleDownload}>
            {t('perfilAgregados.export')}
          </Button>
          <Button asChild size="sm" variant="outline">
            <Link to={`/businesses/${businessId}/sgsst`}>{t('perfilAgregados.back')}</Link>
          </Button>
        </div>
      </div>

      {downloadError && <p className="text-sm text-destructive">{downloadError}</p>}

      {empty ? (
        <div className="surface mx-auto max-w-2xl">
          <ListEmptyState page title={t('perfilAgregados.emptyTitle')} />
        </div>
      ) : (
        tiers.map((tier) => {
          const dists = aggregates.distributions.filter((d) => d.tier === tier)
          if (dists.length === 0) return null
          return (
            <section key={tier} className="space-y-3" data-testid={`perfil-tier-${tier}`}>
              <MicroLabel>{t(tierLabelKey(tier))}</MicroLabel>
              {/* La cita legal no se pliega (U10, T8): debe salir al imprimir y verse de corrido. */}
              {tier === 'salud' && (
                <p className="text-xs text-muted-foreground" data-testid="perfil-agregados-salud">
                  {t('perfilAgregados.saludNote')}
                </p>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                {dists.map((dist) => (
                  <DistributionCard key={dist.variable} dist={dist} valueLabel={valueLabel} />
                ))}
              </div>
            </section>
          )
        })
      )}

      {fichaHasData && (
        <section className="space-y-3" data-testid="perfil-tier-ficha">
          <div className="flex flex-wrap items-center gap-2">
            <MicroLabel>{t('perfilAgregados.tierFicha')}</MicroLabel>
            <PorQue
              label={t('perfilAgregados.deDondeSalen')}
              testId="perfil-agregados-ficha-porque"
              contenidoTestId="perfil-agregados-ficha-porque-contenido">
              {t('perfilAgregados.fichaNote', {active: aggregates.ficha_active_count})}
            </PorQue>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {fichaDists.map((dist) => (
              <DistributionCard
                key={dist.variable}
                dist={dist}
                valueLabel={valueLabel}
                denominatorLine={t('perfilAgregados.fichaDenominator', {
                  n: dist.n,
                  active: aggregates.ficha_active_count,
                })}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

function DistributionCard({
  dist,
  valueLabel,
  denominatorLine,
}: {
  dist: PerfilDistribution
  valueLabel: (variable: string, key: string) => string
  denominatorLine?: string
}) {
  const {t} = useTranslation()
  const entries = Object.entries(dist.counts).sort((a, b) => b[1] - a[1])
  const max = entries.length > 0 ? Math.max(...entries.map(([, count]) => count)) : 0
  return (
    <div className="surface overflow-hidden" data-testid={`perfil-dist-${dist.variable}`}>
      <div className="surface-divider border-b px-4 py-2.5">
        <p className="font-heading text-sm text-foreground">{t(`perfilAgregados.var.${dist.variable}`)}</p>
      </div>
      <div className="px-4 py-3">
        {dist.suppressed ? (
          <p className="text-xs text-muted-foreground" data-testid="perfil-dist-suppressed">
            {t('perfilAgregados.suppressed')}
          </p>
        ) : entries.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('perfilAgregados.noData')}</p>
        ) : (
          <>
            <ul className="space-y-2">
              {entries.map(([key, count]) => (
                <li key={key}>
                  <div className="flex items-baseline justify-between gap-3 text-sm">
                    <span className="min-w-0 truncate text-muted-foreground">{valueLabel(dist.variable, key)}</span>
                    <span className="shrink-0 font-mono text-xs text-foreground">{count}</span>
                  </div>
                  <div aria-hidden="true" className="mt-1 h-1.5 overflow-hidden rounded-full bg-track">
                    <div
                      className="h-full rounded-full bg-primary"
                      style={{width: `${max ? (count / max) * 100 : 0}%`}}
                    />
                  </div>
                </li>
              ))}
            </ul>
            {denominatorLine && (
              <p className="mt-3 font-mono text-[11px] text-muted-foreground" data-testid="perfil-dist-denominator">
                {denominatorLine}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}
